import { useEffect, useState } from 'react'
import { Activity, Loader2, Plus, MessageSquare, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'

interface Service {
  id: number
  slug: string
  name: string
  description: string | null
  status: string
  display_order: number
  is_public: boolean
  last_probed_at: string | null
  created_at: string | null
  updated_at: string | null
}

interface Incident {
  id: number
  title: string
  description: string | null
  severity: string
  status: string
  affected_service_ids: number[]
  started_at: string
  resolved_at: string | null
  created_at: string | null
  updated_at: string | null
}

const SERVICE_STATUSES = ['operational', 'degraded', 'partial_outage', 'major_outage']
const INCIDENT_STATUSES = ['investigating', 'identified', 'fixing', 'resolved', 'postmortem']
const SEVERITIES = ['minor', 'major', 'critical']

const serviceStatusColor = (status: string) => {
  switch (status) {
    case 'operational':
      return 'bg-emerald-500/10 text-emerald-500'
    case 'degraded':
      return 'bg-amber-500/10 text-amber-500'
    case 'partial_outage':
      return 'bg-orange-500/10 text-orange-500'
    case 'major_outage':
      return 'bg-red-500/10 text-red-500'
    default:
      return 'bg-gray-500/10 text-gray-500'
  }
}

const incidentStatusColor = (status: string) => {
  switch (status) {
    case 'resolved':
      return 'bg-emerald-500/10 text-emerald-500'
    case 'investigating':
      return 'bg-amber-500/10 text-amber-500'
    case 'identified':
      return 'bg-blue-500/10 text-blue-500'
    case 'fixing':
      return 'bg-orange-500/10 text-orange-500'
    case 'postmortem':
      return 'bg-purple-500/10 text-purple-500'
    default:
      return 'bg-gray-500/10 text-gray-500'
  }
}

const severityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-500/10 text-red-500'
    case 'major':
      return 'bg-orange-500/10 text-orange-500'
    case 'minor':
      return 'bg-blue-500/10 text-blue-500'
    default:
      return 'bg-gray-500/10 text-gray-500'
  }
}

export function StatusAdmin() {
  const [services, setServices] = useState<Service[]>([])
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<number | null>(null)

  const [createServiceOpen, setCreateServiceOpen] = useState(false)
  const [createIncidentOpen, setCreateIncidentOpen] = useState(false)
  const [updateIncidentId, setUpdateIncidentId] = useState<number | null>(null)

  const [serviceForm, setServiceForm] = useState({
    slug: '',
    name: '',
    description: '',
    status: 'operational',
    display_order: '0',
    is_public: true,
  })

  const [incidentForm, setIncidentForm] = useState({
    title: '',
    description: '',
    severity: 'minor',
    status: 'investigating',
    affected_service_ids: [] as number[],
    started_at: '',
  })

  const [updateForm, setUpdateForm] = useState({
    message: '',
    status: 'investigating',
  })

  const fetchData = async () => {
    setLoading(true)
    try {
      const [statusRes, incidentsRes] = await Promise.all([
        api.get('/admin/status/services'),
        api.get('/status/incidents', { params: { days: 365, per_page: 100 } }),
      ])
      setServices(statusRes.data || [])
      setIncidents(incidentsRes.data.incidents || [])
    } catch {
      toast.error('Failed to load status page data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleServiceStatusChange = async (serviceId: number, newStatus: string) => {
    setActionLoading(serviceId)
    try {
      await api.put(`/admin/status/services/${serviceId}`, { status: newStatus })
      toast.success('Service status updated')
      fetchData()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to update service status'))
    } finally {
      setActionLoading(null)
    }
  }

  const handleCreateService = async () => {
    if (!serviceForm.slug || !serviceForm.name) {
      toast.error('Slug and name are required')
      return
    }
    try {
      await api.post('/admin/status/services', {
        slug: serviceForm.slug,
        name: serviceForm.name,
        description: serviceForm.description || undefined,
        status: serviceForm.status,
        display_order: parseInt(serviceForm.display_order, 10) || 0,
        is_public: serviceForm.is_public,
      })
      toast.success('Service created')
      setCreateServiceOpen(false)
      setServiceForm({
        slug: '',
        name: '',
        description: '',
        status: 'operational',
        display_order: '0',
        is_public: true,
      })
      fetchData()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to create service'))
    }
  }

  const handleCreateIncident = async () => {
    if (!incidentForm.title) {
      toast.error('Title is required')
      return
    }
    try {
      const payload: Record<string, unknown> = {
        title: incidentForm.title,
        description: incidentForm.description || undefined,
        severity: incidentForm.severity,
        status: incidentForm.status,
        affected_service_ids: incidentForm.affected_service_ids,
      }
      if (incidentForm.started_at) {
        payload.started_at = new Date(incidentForm.started_at).toISOString()
      }
      await api.post('/admin/status/incidents', payload)
      toast.success('Incident created')
      setCreateIncidentOpen(false)
      setIncidentForm({
        title: '',
        description: '',
        severity: 'minor',
        status: 'investigating',
        affected_service_ids: [],
        started_at: '',
      })
      fetchData()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to create incident'))
    }
  }

  const handleAddUpdate = async () => {
    if (!updateIncidentId) return
    if (!updateForm.message) {
      toast.error('Message is required')
      return
    }
    try {
      await api.post(`/admin/status/incidents/${updateIncidentId}/updates`, {
        message: updateForm.message,
        status: updateForm.status,
      })
      toast.success('Update added')
      setUpdateIncidentId(null)
      setUpdateForm({ message: '', status: 'investigating' })
      fetchData()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to add update'))
    }
  }

  const handleResolve = async (incidentId: number) => {
    if (!window.confirm('Mark this incident as resolved?')) return
    setActionLoading(incidentId)
    try {
      await api.post(`/admin/status/incidents/${incidentId}/updates`, {
        message: 'Incident resolved.',
        status: 'resolved',
      })
      toast.success('Incident resolved')
      fetchData()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to resolve incident'))
    } finally {
      setActionLoading(null)
    }
  }

  const toggleAffectedService = (serviceId: number) => {
    setIncidentForm((prev) => ({
      ...prev,
      affected_service_ids: prev.affected_service_ids.includes(serviceId)
        ? prev.affected_service_ids.filter((id) => id !== serviceId)
        : [...prev.affected_service_ids, serviceId],
    }))
  }

  const serviceName = (serviceId: number | string) => {
    const id = typeof serviceId === 'string' ? Number(serviceId) : serviceId
    const service = services.find((s) => s.id === id)
    return service?.name || `Service ${serviceId}`
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Status Page
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage status page services and incidents
        </p>
      </div>

      <Tabs defaultValue="services">
        <TabsList>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={createServiceOpen} onOpenChange={setCreateServiceOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  New Service
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Service</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="service-slug">Slug</Label>
                    <Input
                      id="service-slug"
                      value={serviceForm.slug}
                      onChange={(e) => setServiceForm({ ...serviceForm, slug: e.target.value })}
                      placeholder="e.g. mail-api"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service-name">Name</Label>
                    <Input
                      id="service-name"
                      value={serviceForm.name}
                      onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                      placeholder="e.g. Mail API"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service-description">Description</Label>
                    <textarea
                      id="service-description"
                      value={serviceForm.description}
                      onChange={(e) =>
                        setServiceForm({ ...serviceForm, description: e.target.value })
                      }
                      placeholder="Short description"
                      rows={3}
                      className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="service-status">Status</Label>
                      <Select
                        value={serviceForm.status}
                        onValueChange={(value) =>
                          setServiceForm({ ...serviceForm, status: value })
                        }
                      >
                        <SelectTrigger id="service-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICE_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service-order">Display Order</Label>
                      <Input
                        id="service-order"
                        type="number"
                        value={serviceForm.display_order}
                        onChange={(e) =>
                          setServiceForm({ ...serviceForm, display_order: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="service-public"
                      checked={serviceForm.is_public}
                      onCheckedChange={(checked) =>
                        setServiceForm({ ...serviceForm, is_public: checked === true })
                      }
                    />
                    <Label htmlFor="service-public" className="font-normal">
                      Publicly visible
                    </Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateServiceOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateService}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left font-medium">Slug</th>
                      <th className="px-4 py-3 text-left font-medium">Name</th>
                      <th className="px-4 py-3 text-left font-medium">Status</th>
                      <th className="px-4 py-3 text-left font-medium">Order</th>
                      <th className="px-4 py-3 text-left font-medium">Public</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center">
                          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : services.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                          No services configured
                        </td>
                      </tr>
                    ) : (
                      services.map((service) => (
                        <tr
                          key={service.id}
                          className="border-b hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-3 font-medium">{service.slug}</td>
                          <td className="px-4 py-3">
                            {service.name}
                            {service.description && (
                              <div className="text-xs text-muted-foreground">
                                {service.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Select
                              value={service.status}
                              onValueChange={(value) =>
                                handleServiceStatusChange(service.id, value)
                              }
                              disabled={actionLoading === service.id}
                            >
                              <SelectTrigger className="w-40 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SERVICE_STATUSES.map((s) => (
                                  <SelectItem key={s} value={s}>
                                    <Badge
                                      variant="secondary"
                                      className={serviceStatusColor(s)}
                                    >
                                      {s.replace(/_/g, ' ')}
                                    </Badge>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {service.display_order}
                          </td>
                          <td className="px-4 py-3">
                            {service.is_public ? (
                              <Badge variant="default">Yes</Badge>
                            ) : (
                              <Badge variant="secondary">No</Badge>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="incidents" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={createIncidentOpen} onOpenChange={setCreateIncidentOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Create Incident
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Create Incident</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="incident-title">Title</Label>
                    <Input
                      id="incident-title"
                      value={incidentForm.title}
                      onChange={(e) =>
                        setIncidentForm({ ...incidentForm, title: e.target.value })
                      }
                      placeholder="e.g. Mail delivery delays"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="incident-description">Description</Label>
                    <textarea
                      id="incident-description"
                      value={incidentForm.description}
                      onChange={(e) =>
                        setIncidentForm({ ...incidentForm, description: e.target.value })
                      }
                      placeholder="What is happening?"
                      rows={3}
                      className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="incident-severity">Severity</Label>
                      <Select
                        value={incidentForm.severity}
                        onValueChange={(value) =>
                          setIncidentForm({ ...incidentForm, severity: value })
                        }
                      >
                        <SelectTrigger id="incident-severity">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEVERITIES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="incident-status">Status</Label>
                      <Select
                        value={incidentForm.status}
                        onValueChange={(value) =>
                          setIncidentForm({ ...incidentForm, status: value })
                        }
                      >
                        <SelectTrigger id="incident-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INCIDENT_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="incident-started">Started At</Label>
                    <Input
                      id="incident-started"
                      type="datetime-local"
                      value={incidentForm.started_at}
                      onChange={(e) =>
                        setIncidentForm({ ...incidentForm, started_at: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Affected Services</Label>
                    <div className="grid grid-cols-2 gap-2 rounded-md border p-3">
                      {services.length === 0 ? (
                        <div className="col-span-2 text-sm text-muted-foreground">
                          No services available
                        </div>
                      ) : (
                        services.map((service) => (
                          <div key={service.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`affected-${service.id}`}
                              checked={incidentForm.affected_service_ids.includes(service.id)}
                              onCheckedChange={() => toggleAffectedService(service.id)}
                            />
                            <Label
                              htmlFor={`affected-${service.id}`}
                              className="font-normal text-sm"
                            >
                              {service.name}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateIncidentOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateIncident}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left font-medium">Title</th>
                      <th className="px-4 py-3 text-left font-medium">Severity</th>
                      <th className="px-4 py-3 text-left font-medium">Status</th>
                      <th className="px-4 py-3 text-left font-medium">Affected Services</th>
                      <th className="px-4 py-3 text-left font-medium">Started</th>
                      <th className="px-4 py-3 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center">
                          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : incidents.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                          No incidents
                        </td>
                      </tr>
                    ) : (
                      incidents.map((incident) => (
                        <tr
                          key={incident.id}
                          className="border-b hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-3 font-medium">
                            {incident.title}
                            {incident.description && (
                              <div className="text-xs text-muted-foreground truncate max-w-xs">
                                {incident.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="secondary" className={severityColor(incident.severity)}>
                              {incident.severity}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="secondary"
                              className={incidentStatusColor(incident.status)}
                            >
                              {incident.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {incident.affected_service_ids.length === 0 ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {incident.affected_service_ids.map((id) => (
                                  <Badge key={id} variant="outline" className="text-xs">
                                    {serviceName(id)}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {new Date(incident.started_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Dialog
                                open={updateIncidentId === incident.id}
                                onOpenChange={(open) => {
                                  if (open) {
                                    setUpdateIncidentId(incident.id)
                                    setUpdateForm({
                                      message: '',
                                      status: incident.status,
                                    })
                                  } else {
                                    setUpdateIncidentId(null)
                                  }
                                }}
                              >
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="icon" title="Add Update">
                                    <MessageSquare className="h-4 w-4 text-blue-500" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Add Update</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 py-2">
                                    <div className="space-y-2">
                                      <Label htmlFor="update-message">Message</Label>
                                      <textarea
                                        id="update-message"
                                        value={updateForm.message}
                                        onChange={(e) =>
                                          setUpdateForm({ ...updateForm, message: e.target.value })
                                        }
                                        placeholder="Update message"
                                        rows={3}
                                        className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="update-status">Status</Label>
                                      <Select
                                        value={updateForm.status}
                                        onValueChange={(value) =>
                                          setUpdateForm({ ...updateForm, status: value })
                                        }
                                      >
                                        <SelectTrigger id="update-status">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {INCIDENT_STATUSES.map((s) => (
                                            <SelectItem key={s} value={s}>
                                              {s}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <Button
                                      variant="outline"
                                      onClick={() => setUpdateIncidentId(null)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button onClick={handleAddUpdate}>Add Update</Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                              {incident.status !== 'resolved' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Resolve"
                                  onClick={() => handleResolve(incident.id)}
                                  disabled={actionLoading === incident.id}
                                >
                                  {actionLoading === incident.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
